<template>
  <div class="ai-button-wrapper" :class="{ 'ai-button--full-width-wrapper': fullWidth }">
    <button
      :class="buttonClasses"
      :disabled="disabled || loading"
      :style="buttonStyles"
      @click="handleClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div class="ai-button-content">
        <div v-if="loading" class="flex items-center justify-center">
          <div :class="spinnerClasses"></div>
          <span :class="textClasses">{{ loadingText }}</span>
        </div>
        <div v-else class="flex items-center justify-center">
          <feather-icon
            v-if="icon"
            :icon="icon"
            :class="iconClasses"
          />
          <span :class="textClasses">{{ label }}</span>
        </div>
      </div>

      <!-- 툴팁 -->
      <div v-if="tooltip && !disabled" class="ai-tooltip" :class="tooltipClasses">
        {{ tooltip }}
      </div>
    </button>
  </div>
</template>

<script>
export default {
  name: 'AIButton',
  props: {
    // 기본 속성
    label: {
      type: String,
      default: 'AI 분석'
    },
    icon: {
      type: String,
      default: 'ZapIcon'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'AI 분석중...'
    },

    // 스타일 옵션
    size: {
      type: String,
      default: 'medium', // small, medium, large
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    theme: {
      type: String,
      default: 'gradient', // gradient, solid, outline
      validator: value => ['gradient', 'solid', 'outline'].includes(value)
    },
    color: {
      type: String,
      default: 'primary' // primary, secondary, success, warning, danger
    },

    // 고급 기능
    tooltip: {
      type: String,
      default: ''
    },
    fullWidth: {
      type: Boolean,
      default: true
    },
    animated: {
      type: Boolean,
      default: true
    },
    glowEffect: {
      type: Boolean,
      default: true
    },
    padding: {
      type: String,
      default: '', // '8px 16px' 형태로 지정 가능
      validator: value => !value || typeof value === 'string'
    }
  },

  data() {
    return {
      isHovered: false
    }
  },

  computed: {
    buttonClasses() {
      return [
        'ai-button',
        `ai-button--${this.size}`,
        `ai-button--${this.theme}`,
        `ai-button--${this.color}`,
        {
          'ai-button--full-width': this.fullWidth,
          'ai-button--animated': this.animated,
          'ai-button--glow': this.glowEffect && !this.disabled,
          'ai-button--disabled': this.disabled,
          'ai-button--loading': this.loading,
          'ai-button--hovered': this.isHovered
        }
      ]
    },

    buttonStyles() {
      const styles = {}
      if (this.padding) {
        styles.padding = this.padding
      }
      return styles
    },

    textClasses() {
      return [
        'ai-button-text',
        `ai-button-text--${this.size}`
      ]
    },

    iconClasses() {
      return [
        'ai-button-icon',
        `ai-button-icon--${this.size}`
      ]
    },

    spinnerClasses() {
      return [
        'ai-spinner',
        `ai-spinner--${this.size}`,
        `ai-spinner--${this.color}`
      ]
    },

    tooltipClasses() {
      return [
        {
          'ai-tooltip--visible': this.isHovered && this.tooltip
        }
      ]
    }
  },

  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    },

    handleMouseEnter() {
      this.isHovered = true
    },

    handleMouseLeave() {
      this.isHovered = false
    }
  }
}
</script>

<style scoped lang="scss">
.ai-button-wrapper {
  position: relative;
  display: inline-block;
  width: auto;

  // 전체 너비일 때만 100% 적용
  &.ai-button--full-width-wrapper {
    width: 100%;
  }

  // CSS 변수 정의
  --ai-border-width: 1px;
}

.ai-button {
  position: relative;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  overflow: hidden;

  // 크기 variants
  &--small {
    height: 36px;
    padding: 6px 12px;
    border-radius: 6px;
  }

  &--medium {
    height: 44px;
    padding: 8px 16px;
    border-radius: 8px;
  }

  &--large {
    height: 52px;
    padding: 10px 20px;
    border-radius: 10px;
  }

  // 전체 너비
  &--full-width {
    width: 100%;
  }

      // 테마 variants - Gradient (AI 스타일 그라데이션 테두리)
  &--gradient {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #667eea;
    position: relative;

    &.ai-button--small {
      padding: 1px;
    }

    &.ai-button--medium {
      padding: 1px;
    }

    &.ai-button--large {
      padding: 2px;
    }

    // 내부 흰색 배경
    .ai-button-content {
      background: white;
      border-radius: inherit;
      margin: var(--ai-border-width);
      height: calc(100% - calc(var(--ai-border-width) * 2));
      padding: 0 16px; // 내부 content에 padding 적용
    }
  }

  // 테마 variants - Solid
  &--solid {
    &.ai-button--primary {
      background: #667eea;
      color: white;
    }

    &.ai-button--secondary {
      background: #6c757d;
      color: white;
    }

    &.ai-button--success {
      background: #28a745;
      color: white;
    }

    &.ai-button--warning {
      background: #fd7e14;
      color: white;
    }

    &.ai-button--danger {
      background: #dc3545;
      color: white;
    }
  }

  // 테마 variants - Outline
  &--outline {
    background: white;
    border: 2px solid;

    &.ai-button--primary {
      border-color: #667eea;
      color: #667eea;
    }

    &.ai-button--secondary {
      border-color: #6c757d;
      color: #6c757d;
    }

    &.ai-button--success {
      border-color: #28a745;
      color: #28a745;
    }

    &.ai-button--warning {
      border-color: #fd7e14;
      color: #fd7e14;
    }

    &.ai-button--danger {
      border-color: #dc3545;
      color: #dc3545;
    }
  }

  // Glow 효과 (통일된 보라색 계열)
  &--glow {
    box-shadow: 0 2px 8px 0 rgba(102, 126, 234, 0.2);
  }

  // 애니메이션 효과 (통일된 보라색 계열)
  &--animated {
    &:hover:not(.ai-button--disabled):not(.ai-button--loading) {
      transform: translateY(-2px);

      &.ai-button--glow {
        box-shadow: 0 4px 12px 0 rgba(102, 126, 234, 0.3);
      }
    }

    &:active:not(.ai-button--disabled):not(.ai-button--loading) {
      transform: translateY(0);
    }
  }

  // Disabled 상태
  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;

    .ai-button-content {
      color: #999 !important;
    }
  }

  // Loading 상태
  &--loading {
    cursor: not-allowed;
  }
}

// 버튼 내용
.ai-button-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Gradient 테마에서 크기별 border-radius 조정
.ai-button--gradient {
  &.ai-button--small .ai-button-content {
    border-radius: calc(6px - var(--ai-border-width));
  }

  &.ai-button--medium .ai-button-content {
    border-radius: calc(8px - var(--ai-border-width));
  }

  &.ai-button--large .ai-button-content {
    border-radius: calc(10px - var(--ai-border-width));
  }
}

// 텍스트 스타일
.ai-button-text {
  font-weight: 600;

  &--small {
    font-size: 12px;
  }

  &--medium {
    font-size: 14px;
  }

  &--large {
    font-size: 16px;
  }
}

// 아이콘 스타일
.ai-button-icon {
  margin-right: 8px;

  &--small {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  &--medium {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  &--large {
    width: 18px;
    height: 18px;
    margin-right: 10px;
  }
}

// 스피너 스타일 (통일된 보라색 계열)
.ai-spinner {
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  border-top-color: #667eea;
  animation: ai-spin 0.8s linear infinite;
  margin-right: 8px;

  &--small {
    width: 14px;
    height: 14px;
    margin-right: 6px;
  }

  &--medium {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  &--large {
    width: 18px;
    height: 18px;
    margin-right: 10px;
  }
}

// 툴팁 스타일
.ai-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  margin-bottom: 5px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.8);
  }

  &--visible {
    opacity: 1;
    visibility: visible;
  }
}

@keyframes ai-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
